import { compact, identity } from 'lodash-es';
import { useMemo } from 'react';
import { useSecuritiesContext } from '../../../../contexts';
import type { Security } from '../../../../types';
import { IconName } from '../../../Icons';

/**
 * Creates a filter property definition for selecting currencies that match
 * the base or quote currency of a given symbol.
 *
 * @param availableSecurities - Optional list of securities to filter currencies. If omitted, all securities from the Securities Context will be used.
 * @returns A filter property definition for symbol-based currency filtering.
 */
export function useSymbolCurrencyFilter(
  availableSecurities?: Pick<Security, 'Symbol' | 'BaseCurrency' | 'QuoteCurrency'>[]
) {
  const { securitiesList: _securitiesList } = useSecuritiesContext();
  const securitiesList = availableSecurities ?? _securitiesList;
  const securitiesBySymbol = useMemo(() => new Map(securitiesList.map(sec => [sec.Symbol, sec])), [securitiesList]);

  const uniqueCurrencies = useMemo(
    () => Array.from(new Set(securitiesList.map(sec => compact([sec.BaseCurrency, sec.QuoteCurrency])).flat())),
    [securitiesList]
  );
  return useMemo(
    () => ({
      key: 'Currencies' as const,
      field: 'Symbol',
      fieldFilter: (symbol: string, selections: string[]) => {
        if (securitiesBySymbol.has(symbol)) {
          const { BaseCurrency, QuoteCurrency } = securitiesBySymbol.get(symbol)!;
          return (BaseCurrency && selections.includes(BaseCurrency)) || selections.includes(QuoteCurrency);
        }
        return false;
      },
      label: 'Currency',
      icon: IconName.CurrencyDollar,
      options: uniqueCurrencies,
      getOptionLabel: identity,
    }),
    [securitiesBySymbol, uniqueCurrencies]
  );
}
